































































import {
  IresDeviceRequestType,
  PanelRequestStatus,
} from "../../data/models/PanelRequest";
import {
  createPanelActionRequest,
  savePanelRequest,
} from "../../data/repository/PanelRequestRepository";
import {
  buildStartPanelFrame,
  buildReadProgramFrame,
  buildReadConfigFrame,
  buildFactoryResetFrame,
  buildStopPanelCommand,
  buildLoadRunsFrame,
} from "../../utils/panel/commands/CommandBuilder";
import { Vue, Component, Prop, Ref } from "vue-property-decorator";
import { Panel } from "../../data/models/Panel";
import {
  PanelConfig,
  getConfigForPanelType,
  getStartProgram,
  Program,
} from "../../data/models/PanelConfig";

import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import ProgramChooserComponent from "./ProgramChooser.vue";
import {
  getIresDeviceGeneration,
  IresDeviceGeneration,
  isIdle,
} from "@/data/models/IresDevice";
import LoadRunsDialogComponent from "./LoadRunsDialog.vue";
import { getActiveUser } from "@/data/repository/UserRepository";
import { forceLoadRunsViaIotAccess } from "@/permissions/UserPermissions";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    "program-chooser": ProgramChooserComponent,
    "load-runs": LoadRunsDialogComponent,
  },
})
export default class PanelControlComponent extends Vue {
  @Prop() panel!: Panel;
  @Prop({ default: null }) config!: PanelConfig | null;
  @Prop({ default: false }) showSetIresDeviceOfflineButton!: boolean;

  @Ref("confirmPanelAction")
  confirmationDialog!: ConfirmationDialogComponent;

  @Ref("programChooser")
  programChooser!: ProgramChooserComponent;

  @Ref("loadRuns")
  loadRunsDialog: LoadRunsDialogComponent;

  showingSuccessSnackbar = false;
  successSnackbarTimeout = 5000;
  successSnackbarText = "";

  showingStartStopPanelDialog = false;

  /** Phones: the command buttons are small, the size of the view buttons below (#2610011). */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  get showIresDeviceControl() {
    return (
      this.panel !== null &&
      (
        getIresDeviceGeneration(this.panel) === IresDeviceGeneration.D ||
        getIresDeviceGeneration(this.panel) === IresDeviceGeneration.E
      )
    );
  }

  get activeUser() {
    return getActiveUser();
  }

  get isAllowedToForceLoadRuns() {
    return forceLoadRunsViaIotAccess(this.activeUser)
  }

  get isPanelOnline() {
    return this.panel.panelStatus?.online ?? false;
  }

  onSetPanelOffline() {
    this.$emit("setIresDeviceOffline");
  }

  async queueProgramStart(
    program: Program,
    requestType: IresDeviceRequestType
  ) {
    const payload = buildStartPanelFrame(program.programNumber);

    await createPanelActionRequest(
      this.panel.serialNo,
      payload,
      this.isPanelOnline,
      requestType
    );

    this.onPanelRequestCreated(requestType);
  }

  async queueLoadRuns(
    programRunId: number
  ) {
    const requestType = IresDeviceRequestType.LOAD_RUNS;
    const payload = buildLoadRunsFrame(programRunId);

    await createPanelActionRequest(
      this.panel.serialNo,
      payload,
      this.isPanelOnline,
      requestType
    );

    this.onPanelRequestCreated(requestType);
  }

  async queuePanelStop() {
    const payload = buildStopPanelCommand();
    const requestType = IresDeviceRequestType.STOP_PROGRAM;

    await createPanelActionRequest(
      this.panel.serialNo,
      payload,
      this.isPanelOnline,
      requestType
    );

    this.onPanelRequestCreated(requestType);
  }

  async onReadConfig() {
    const readProgramFrames: string[] = [];

    for (let i = 0; i < 8; i++) {
      readProgramFrames.push(buildReadProgramFrame(i));
    }

    const readConfigFrame = buildReadConfigFrame();

    await Promise.all([
      savePanelRequest({
        requestId: `${this.panel.serialNo}_READ_CONFIG`,
        payload: readConfigFrame,
        priority: 1001,
        canBeObserved: false,
        panelId: this.panel.serialNo,
        forwardImmediately: false,
        createdAt: new Date(),
        requestStatus: PanelRequestStatus.PENDING,
      }),
      ...readProgramFrames.map((frame, index) =>
        savePanelRequest({
          requestId: `${this.panel.serialNo}_READ_PROGRAM_${index}`,
          payload: frame,
          priority: 1000,
          canBeObserved: false,
          panelId: this.panel.serialNo,
          forwardImmediately: false,
          createdAt: new Date(),
          requestStatus: PanelRequestStatus.PENDING,
        })
      ),
    ]);
  }

  async onFactoryReset() {
    const factoryResetFrame = buildFactoryResetFrame();

    await savePanelRequest({
      requestId: `${this.panel.serialNo}_FACTORY_RESET`,
      payload: factoryResetFrame,
      priority: 1002,
      canBeObserved: false,
      panelId: this.panel.serialNo,
      forwardImmediately: false,
      createdAt: new Date(),
      requestStatus: PanelRequestStatus.PENDING,
    });
  }

  async onReadPrograms() {
    const payloads: string[] = [];

    for (let i = 0; i < 8; i++) {
      payloads.push(buildReadProgramFrame(i));
    }

    await Promise.all([
      payloads.map((payload, index) =>
        savePanelRequest({
          requestId: `${this.panel.serialNo}_READ_PROGRAM_${index}`,
          payload: payload,
          priority: 1000,
          canBeObserved: false,
          panelId: this.panel.serialNo,
          forwardImmediately: false,
          createdAt: new Date(),
          requestStatus: PanelRequestStatus.PENDING,
        })
      ),
    ]);
  }

  async onChooseProgram() {
    const config = this.config;

    if (!config) {
      return;
    }

    const panelConfig = getConfigForPanelType(config, this.panel);

    if (!panelConfig) {
      return;
    }

    const programsToChooseFrom = [];

    for (const program of panelConfig.programs.values()) {
      if (program.programNumber > 0 && program.programNumber < 4) {
        programsToChooseFrom.push(program);
      }
    }

    const selectedProgram = await this.programChooser.show(
      programsToChooseFrom
    );

    if (selectedProgram) {
      await this.onStartProgram(
        selectedProgram,
        IresDeviceRequestType.SELECT_PROGRAM
      );
    }
  }

  async onShowStartStopPanelConfirmation() {
    if (this.isPanelIdle) {
      await this.onStartProgram(
        this.startProgram,
        IresDeviceRequestType.START_PROGRAM
      );
    } else {
      await this.onStopProgram();
    }
  }

  async onStartProgram(program: Program, requestType: IresDeviceRequestType) {
    const title = this.$t("panelControl.startPanelTitle", [
      this.panel.serialNo,
      program.programNumber,
      program.name,
    ]).toString();

    const didConfirm = await this.askForPanelActionConfirmation(title);

    if (didConfirm) {
      await this.queueProgramStart(program, requestType);
    }
  }

  async onLoadRuns() {

    const {
      programRunId
    } = await this.loadRunsDialog.show();

    await this.queueLoadRuns(programRunId);
  }

  async onStopProgram() {
    const title = this.$t("panelControl.stopPanelTitle", [
      this.panel.serialNo,
    ]).toString();

    const didConfirm = await this.askForPanelActionConfirmation(title);

    if (didConfirm) {
      await this.queuePanelStop();
    }
  }

  async askForPanelActionConfirmation(title: string) {
    const acceptMessge = this.$t("common.execute").toString();
    const message =
      this.$t(
        this.isPanelOnline
          ? "panelControl.executingCommandImmediatly"
          : "panelControl.executingCommandNextIotConnection"
      ).toString() +
      " " +
      this.$t("common.areYouSure").toString();

    return await this.confirmationDialog.show({
      title: title,
      message: message,
      acceptMessage: acceptMessge,
    });
  }

  onPanelRequestCreated(requestType: IresDeviceRequestType) {
    let message = "";

    switch (requestType) {
      case IresDeviceRequestType.START_PROGRAM:
      case IresDeviceRequestType.SELECT_PROGRAM:
        message = this.$t("panelControl.startQueued").toString();
        break;
      case IresDeviceRequestType.STOP_PROGRAM:
        message = this.$t("panelControl.stopQueued").toString();
        break;
      case IresDeviceRequestType.LOAD_RUNS:
        message = this.$t("panelControl.loadRunsQueued").toString();
        break;
    }

    this.successSnackbarText = message;
    this.showingSuccessSnackbar = true;
  }

  onDontStartStopPanel() {
    this.showingStartStopPanelDialog = false;
  }

  get startProgram(): Program {
    const config = this.config;

    if (config) {
      const startProgram = getStartProgram(config, this.panel);
      if (startProgram) {
        return startProgram;
      }
    }

    return {
      name: "",
      description: "",
      programNumber: 1,
    };
  }

  get arePanelControlsDisabled() {
    const panelStatus = this.panel.panelStatus;

    if (!panelStatus) {
      return false;
    }

    const ipuStatus = panelStatus.ipuStatus;
    const isInWarningOrError = ipuStatus >= 4 && ipuStatus <= 7;
    const isMissingConfig = this.config === null;

    return isMissingConfig || isInWarningOrError;
  }

  get isPanelIdle(): boolean {
    return isIdle(this.panel);
  }

  get startStopButtonText(): string {
    return this.$t(
      this.isPanelIdle
        ? "panelDetail.actions.startPanel"
        : "panelDetail.actions.stopPanel"
    ).toString();
  }

  async setPanelOffline() {
    this.$emit("setIresDeviceOffline");
  }
}
